import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../components/ui/use-toast';
import { api } from '../services/api';
import { Copy, ExternalLink } from 'lucide-react';

const AffiliateDashboard = () => {
  const [affiliateCode, setAffiliateCode] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleLoadStats = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await api.getAffiliateStats(affiliateCode);
      setStats(data);
      localStorage.setItem('affiliate_code', affiliateCode);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Invalid affiliate code or not found',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const savedCode = localStorage.getItem('affiliate_code');
    if (savedCode) {
      setAffiliateCode(savedCode);
    }
  }, []);

  const getAffiliateLink = () => {
    const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
    return `${frontendUrl}?ref=${affiliateCode}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      description: 'Link copied to clipboard'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-red-100 text-red-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          Affiliate Dashboard
        </h1>

        {!stats ? (
          <Card className="p-8 max-w-md mx-auto">
            <h2 className="text-xl font-semibold mb-4">Enter Your Affiliate Code</h2>
            <form onSubmit={handleLoadStats} className="space-y-4">
              <div>
                <Label>Affiliate Code</Label>
                <Input
                  value={affiliateCode}
                  onChange={(e) => setAffiliateCode(e.target.value.toUpperCase())}
                  placeholder="JOHN1234"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Loading...' : 'View Dashboard'}
              </Button>
            </form>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Header */}
            <Card className="p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold mb-2">{stats.affiliate.name}</h2>
                  <p className="text-gray-600 mb-2">{stats.affiliate.email}</p>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-lg font-bold">
                      {stats.affiliate.affiliate_code}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${getStatusBadge(stats.affiliate.status)}`}>
                      {stats.affiliate.status}
                    </span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setStats(null);
                    localStorage.removeItem('affiliate_code');
                  }}
                >
                  Logout
                </Button>
              </div>
            </Card>

            {/* Stats */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card className="p-6">
                <p className="text-sm text-gray-600 mb-1">Commission Rate</p>
                <p className="text-3xl font-bold text-blue-600">
                  {stats.affiliate.commission_rate}%
                </p>
              </Card>

              <Card className="p-6">
                <p className="text-sm text-gray-600 mb-1">Total Referrals</p>
                <p className="text-3xl font-bold">{stats.affiliate.total_referrals || 0}</p>
              </Card>

              <Card className="p-6">
                <p className="text-sm text-gray-600 mb-1">Total Sales</p>
                <p className="text-3xl font-bold text-green-600">
                  ${(stats.affiliate.total_sales || 0).toFixed(2)}
                </p>
              </Card>

              <Card className="p-6">
                <p className="text-sm text-gray-600 mb-1">Total Commission</p>
                <p className="text-3xl font-bold text-purple-600">
                  ${(stats.affiliate.total_commission || 0).toFixed(2)}
                </p>
              </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-6">
                <p className="text-sm text-gray-600 mb-1">Pending Commission</p>
                <p className="text-2xl font-bold text-orange-600">
                  ${(stats.affiliate.pending_commission || 0).toFixed(2)}
                </p>
              </Card>

              <Card className="p-6">
                <p className="text-sm text-gray-600 mb-1">Paid Commission</p>
                <p className="text-2xl font-bold text-green-600">
                  ${(stats.affiliate.paid_commission || 0).toFixed(2)}
                </p>
              </Card>
            </div>

            {/* Affiliate Link */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Your Affiliate Link</h3>
              <div className="flex gap-2">
                <Input
                  value={getAffiliateLink()}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(getAffiliateLink())}
                >
                  <Copy className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open(getAffiliateLink(), '_blank')}
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Share this link to earn commissions on sales!
              </p>
            </Card>

            {/* Referrals */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Recent Referrals</h3>
              {stats.referrals.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  No referrals yet. Start sharing your link!
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Order</th>
                        <th className="text-left py-2">Date</th>
                        <th className="text-right py-2">Order Total</th>
                        <th className="text-right py-2">Commission</th>
                        <th className="text-center py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.referrals.map((referral) => (
                        <tr key={referral._id} className="border-b">
                          <td className="py-2 font-mono text-sm">{referral.order_number}</td>
                          <td className="py-2 text-sm">
                            {new Date(referral.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-2 text-right">${referral.order_total.toFixed(2)}</td>
                          <td className="py-2 text-right font-semibold text-green-600">
                            ${referral.commission_amount.toFixed(2)}
                          </td>
                          <td className="py-2 text-center">
                            <span
                              className={`text-xs px-2 py-1 rounded ${
                                referral.status === 'paid'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-orange-100 text-orange-800'
                              }`}
                            >
                              {referral.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateDashboard;
